import React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Alert, Box, Container, useMediaQuery } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { setChainType } from "./redux/walletSlice";
import { useAppDispatch, useAppSelector } from "./hooks/hooks";
import { Helmet } from "react-helmet";
import AppLayout from "./components/AppLayout/AppLayout";
import { ConnectionProvider } from "./contexts/ConnectionContext";
import { SecurityProvider } from "./contexts/SecurityContext";

const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || "");

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#9147ff",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
  },
  typography: {
    fontFamily: "Inter, Arial, sans-serif",
  },
});

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm" sx={{ mt: 1 }}>
          <Alert severity="error">
            Something went wrong. Please refresh the page.
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}

function App() {
  const dispatch = useAppDispatch();
  const { chainType } = useAppSelector((state) => state.wallet);
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);

  React.useEffect(() => {
    const envChainType = process.env.REACT_APP_BLOCKCHAIN_NETWORK;
    dispatch(
      setChainType(envChainType !== undefined ? envChainType : "solana")
    );
  }, [dispatch]);

  return (
    <Elements stripe={stripePromise}>
      <SecurityProvider>
        <ConnectionProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  minHeight: "100dvh",
                  backgroundColor: theme.palette.background.default,
                  // Mobile styles
                  ...(isMobileView && {
                    alignItems: "flex-start",
                    padding: 0,
                  }),
                  // Desktop styles
                  ...(!isMobileView && {
                    alignItems: "center",
                    padding: 2,
                  }),
                }}
              >
                <ErrorBoundary>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: MOBILE_APP_WIDTH,
                      height: isMobileView ? "100dvh" : "90vh",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: theme.palette.background.paper,
                      // Mobile styling
                      ...(isMobileView && {
                        borderRadius: 0,
                        boxShadow: "none",
                      }),
                      // Desktop styling
                      ...(!isMobileView && {
                        borderRadius: 2,
                        boxShadow: 6,
                      }),
                    }}
                  >
                    <Helmet>
                      <title>
                        {chainType === "evm" ? "Ndeipi" : "Believe In Unity"}
                      </title>
                      <meta name="apple-mobile-web-app-capable" content="yes" />
                      <meta
                        name="apple-mobile-web-app-status-bar-style"
                        content="black-translucent"
                      />
                      <meta
                        name="apple-mobile-web-app-title"
                        content={
                          chainType === "evm" ? "Ndeipi" : "Believe In Unity"
                        }
                      />
                      <link
                        rel="apple-touch-icon"
                        href="/icons/icon-192x192.png"
                      />
                    </Helmet>
                    <AppLayout />
                  </Box>
                </ErrorBoundary>
              </Box>
            </ThemeProvider>
          </LocalizationProvider>
        </ConnectionProvider>
      </SecurityProvider>
    </Elements>
  );
}

export default App;
